import React from 'react';
import 'twin.macro';
import {
  Periode,
  Matiere as IMatiere,
} from '@teetsh/app/src/types/programmations';
import { HeaderItem } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/components/HeaderItem';
import { MatiereLayout } from './components/MatiereLayout';
import { LabelledDateRange } from '@teetsh/app/src/types/zone';
import {
  PeriodeWithWeeks,
  groupWeeksByPeriode,
} from '../utils/groupWeeksByPeriodes';
import { getPeriodeColor } from '../New/getColors';
import { Row, IRow } from './components/Row';
import { Header } from './components/Row';
import { useTranslation } from 'react-i18next';
import _orderBy from '@teetsh/app/src/utils/arrays/orderBy';
import { getPureReferentialItemName } from '@teetsh/app/src/components/Bo/ReferentialItemElement';

export function buildHeaders(weeksByPeriodes: PeriodeWithWeeks[]): Header[] {
  return weeksByPeriodes.map((p, idx) => ({
    name: p.periode.label,
    color: getPeriodeColor(idx),
  }));
}

export function buildRows(
  matiere: IMatiere,
  weeksByPeriodes: PeriodeWithWeeks[],
  weekLabelFt: (n: number) => string,
): IRow[] {
  const weeksByWeekNumber: Record<number, Periode[]> = weeksByPeriodes.reduce(
    (acc, p) => {
      p.weeks.forEach((w, idx) => {
        if (!acc[idx + 1]) {
          acc[idx + 1] = [];
        }
        acc[idx + 1].push(w);
      });
      return acc;
    },
    {} as Record<number, Periode[]>,
  );

  return Object.keys(weeksByWeekNumber).map(weekNumberKey => {
    const weekNumber = parseInt(weekNumberKey);
    return {
      header: {
        name: weekLabelFt(weekNumber),
        color: getPeriodeColor(weekNumber - 1),
      },
      subrows: matiere.domaines.map(d => ({
        header: { name: getPureReferentialItemName(d.name), color: d.color },
        blocks: weeksByPeriodes.map(p => {
          const currPeriode = p.weeks[weekNumber - 1];
          return {
            items: _orderBy(
              d.periodContents
                .filter(pc => pc.periodeId === currPeriode?.id)
                .flatMap(v => v.items),
              'y',
              ['asc'],
            ),
          };
        }),
      })),
    };
  });
}

interface Props {
  matiere: IMatiere;
  periodes: Periode[];
  schoolyearPeriodes: LabelledDateRange[];
  preventCutting: boolean;
  breakBefore: boolean;
}

export function PeriodeSemaineView(props: Props) {
  const { matiere, periodes, schoolyearPeriodes } = props;
  const { weeksByPeriodes: weeksByPeriodesFound = [], otherWeeks = [] } =
    groupWeeksByPeriode(periodes, schoolyearPeriodes);
  const { t } = useTranslation(['programmation']);
  const otherWeeksWithContent = otherWeeks.filter(w =>
    matiere.domaines.some(d =>
      d.periodContents.some(pc => pc.periodeId === w.id),
    ),
  );

  const weeksByPeriodes = [
    ...weeksByPeriodesFound,
    ...(otherWeeksWithContent.length
      ? [
          {
            periode: { id: 'other', label: t('programmation:periodeUnknown') },
            weeks: otherWeeksWithContent,
          },
        ]
      : []),
  ];

  const headers = buildHeaders(weeksByPeriodes);
  const rows = buildRows(matiere, weeksByPeriodes, n =>
    t('programmation:weekX', { count: n }),
  );

  return (
    <MatiereLayout rowTitleOrientation="vertical">
      {headers.map((h, idx) => (
        <div tw="flex-1 min-w-0" key={idx}>
          <HeaderItem value={h.name} color={h.color} />
        </div>
      ))}
      {rows.map((r, idx) => (
        <Row
          preventCutting={props.preventCutting}
          breakBefore={props.breakBefore}
          key={idx}
          header={r.header}
          subrows={r.subrows}
          rowTitleOrientation="vertical"
        />
      ))}
    </MatiereLayout>
  );
}
